import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';

import { TableHead } from '@/components/cargofl-ui';
import { Hint } from '@/components/cargofl-ui';
import { cn } from '@/lib/utils';

export type SortDirection = 'asc' | 'desc';

interface SortableHeadProps<T extends string> {
  /** The API's sort key. */
  column: T;
  sort: T;
  direction: SortDirection;
  onSort: (column: T, direction: SortDirection) => void;
  /** Match the column's own alignment - a right-aligned number wants its control there too. */
  align?: 'left' | 'right';
  className?: string;
  children: React.ReactNode;
}

/**
 * A column header that sorts. Clicking an inactive column sorts it ascending;
 * clicking the active one flips the direction. There is deliberately no third
 * "unsorted" click - the list is always ordered by something, so a cycle back to
 * no-sort would just mean "back to the default" via a state nobody can name.
 *
 * The label is `text-foreground`, not the table's usual muted grey: at muted the
 * header and its sort arrow were both too faint to read as a control at all.
 */
export function SortableHead<T extends string>({
  column,
  sort,
  direction,
  onSort,
  align = 'left',
  className,
  children,
}: SortableHeadProps<T>) {
  const active = sort === column;
  const Icon = !active ? ChevronsUpDown : direction === 'asc' ? ArrowUp : ArrowDown;

  return (
    <TableHead
      className={cn('p-0', className)}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <Hint label={'Sort by this column'}><button
        type="button"
        className={cn(
          'flex h-10 w-full items-center gap-1 px-2 font-medium text-foreground',
          align === 'right' ? 'justify-end' : 'justify-start text-left',
        )}
        onClick={() => onSort(column, active && direction === 'asc' ? 'desc' : 'asc')}
      >
        {children}
        <Icon
          className={cn(
            'size-3.5 shrink-0',
            active ? 'text-foreground' : 'text-muted-foreground',
          )}
        />
      </button></Hint>
    </TableHead>
  );
}
